import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { useStripe } from '@/hooks/useStripe';
import { useState } from 'react';
import { toast } from 'sonner';

interface TeamMemberUpgradeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentMemberCount: number;
}

export const TeamMemberUpgradeDialog = ({
  isOpen,
  onClose,
  currentMemberCount,
}: TeamMemberUpgradeDialogProps) => {
  const { upgradeTeamMembers } = useStripe();
  const [additionalMembers, setAdditionalMembers] = useState(1);
  const pricePerMember = 9; // £9 per member per month

  const handleUpgrade = async () => {
    try {
      const response = await upgradeTeamMembers.mutateAsync({
        additional_members: additionalMembers,
        return_url: window.location.href,
      });

      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Failed to upgrade team members:', error);
      toast.error('Failed to process upgrade. Please try again.');
    }
  };

  const totalPrice = additionalMembers * pricePerMember;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add More Team Members</DialogTitle>
          <DialogDescription>
            You currently have {currentMemberCount} team members. Add more members
            to your team for £{pricePerMember}/month per additional member.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Additional Members:</span>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setAdditionalMembers((prev) => Math.max(1, prev - 1))
                }
                disabled={additionalMembers <= 1}>
                -
              </Button>
              <span className="w-8 text-center">{additionalMembers}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAdditionalMembers((prev) => prev + 1)}>
                +
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span>Total monthly cost:</span>
            <span className="font-semibold">£{totalPrice}/month</span>
          </div>
          <Button
            onClick={handleUpgrade}
            className="w-full"
            isLoading={upgradeTeamMembers.isPending}>
            Upgrade for £{totalPrice}/month
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 